import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Crown, Sparkles } from 'lucide-react';
import { tierData } from '../data/mock';
import { useCurrency } from '../context/CurrencyContext';
import { trackInitiateCheckout, trackViewContent } from '../utils/metaPixel';

const CurrencyToggle = () => {
  const { currency, toggleCurrency } = useCurrency();
  
  return (
    <div className="flex items-center justify-center gap-3 mb-10">
      <span className={`text-sm font-medium transition-colors ${currency === 'INR' ? 'text-[var(--text-primary)]' : 'text-[var(--text-light)]'}`}>
        INR
      </span>
      <button 
        onClick={toggleCurrency}
        className="relative w-14 h-7 bg-[var(--bg-secondary)] border border-[var(--border-light)] rounded-full transition-colors cursor-pointer"
        aria-label="Toggle currency"
      >
        <span 
          className={`absolute top-1 w-5 h-5 bg-[var(--gold-primary)] rounded-full transition-all duration-300 ${
            currency === 'USD' ? 'left-8' : 'left-1'
          }`}
        />
      </button>
      <span className={`text-sm font-medium transition-colors ${currency === 'USD' ? 'text-[var(--text-primary)]' : 'text-[var(--text-light)]'}`}>
        USD
      </span>
    </div>
  );
};

const TierCard = ({ tier }) => {
  const { currency } = useCurrency();
  const isPremium = tier.premium;
  const isHighlighted = tier.highlighted;
  const isBestValue = tier.bestValue;
  const hasGoldAccent = tier.goldAccent;
  const showWhatsAppFilter = tier.cta.toLowerCase().includes('whatsapp');
  const isInternalLink = tier.isInternalLink;
  const hasBadge = tier.badge;
  
  // Get price based on currency
  const price = currency === 'USD' ? tier.priceUSD : tier.priceINR;
  
  return (
    <div 
      className={`tier-card hover-lift relative ${isHighlighted ? 'highlighted' : ''} ${isPremium ? 'premium premium-enhanced' : ''} ${hasGoldAccent ? 'gold-accent' : ''}`}
      id={tier.id === 'sovereign' ? 'sovereign' : undefined}
    >
      {/* Best Value Badge */}
      {isBestValue && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span className="inline-flex items-center gap-1 px-4 py-1 bg-[var(--gold-dark)] text-[var(--bg-primary)] text-xs font-semibold tracking-wider uppercase">
            <Sparkles size={12} />
            Most Chosen
          </span>
        </div>
      )}
      
      {/* Most Popular Badge */}
      {hasBadge && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span className="inline-flex items-center gap-1 px-4 py-1 bg-[var(--gold-dark)] text-[var(--bg-primary)] text-xs font-semibold tracking-wider uppercase">
            {hasBadge}
          </span>
        </div>
      )}
      
      {/* Tier Label */}
      <div className="flex items-center gap-2 mb-5">
        {isPremium && <Crown size={16} className="text-[var(--gold-primary)]" />}
        <span className={`body-small uppercase tracking-widest ${isPremium ? 'text-[var(--gold-primary)]' : 'text-[var(--text-light)]'}`}>
          {tier.tier}
        </span>
      </div>
      
      {/* Premium Labels for Sovereign */}
      {tier.labels && (
        <div className="flex flex-wrap gap-2 mb-5">
          {tier.labels.map((label, index) => (
            <span key={index} className="text-xs px-2 py-1 border border-[var(--gold-primary)] text-[var(--gold-primary)] rounded-sm">
              {label}
            </span>
          ))}
        </div>
      )}
      
      {/* Invitation-Only Separator for Sovereign */}
      {isPremium && (
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-[var(--gold-primary)] opacity-30"></div>
          <span className="text-xs text-[var(--gold-primary)] uppercase tracking-wider">Invitation Only</span>
          <div className="flex-1 h-px bg-[var(--gold-primary)] opacity-30"></div>
        </div>
      )}
      
      {/* Name */}
      <h3 className={`heading-3 mb-4 ${isPremium ? 'text-[var(--text-inverse)]' : ''}`}>
        {tier.name}
      </h3>
      
      {/* Price - only show if not null */}
      {price && (
        <div className="mb-3">
          <span className={`heading-2 ${isPremium ? 'text-[var(--gold-primary)]' : 'text-[var(--gold-primary)]'}`}>
            {price}
          </span>
        </div>
      )}
      
      {/* Duration */}
      <p className={`body-small mb-6 ${isPremium ? 'text-[var(--gold-primary)] font-medium' : 'text-[var(--text-light)]'}`}>
        {tier.duration}
      </p>
      
      {/* Hook (one-liner) */}
      {tier.hook && (
        <p className={`text-sm font-medium mb-6 leading-relaxed ${isPremium ? 'text-[var(--text-inverse)]' : 'text-[var(--text-primary)]'}`}>
          {tier.hook}
        </p>
      )}
      
      {/* Headline (for Bundle/Sovereign) */}
      {tier.headline && (
        <p className={`text-base font-medium mb-3 leading-relaxed ${isPremium ? 'text-[var(--text-inverse)]' : 'text-[var(--text-primary)]'}`}>
          {tier.headline}
        </p>
      )}
      
      {/* Subline */}
      {tier.subline && (
        <p className={`text-sm italic mb-6 leading-relaxed ${isPremium ? 'text-[var(--gold-primary)]' : 'text-[var(--text-secondary)]'}`}>
          {tier.subline}
        </p>
      )}
      
      {/* Tagline for Bundle */}
      {tier.tagline && (
        <p className="text-xs text-[var(--text-secondary)] italic mb-5">
          {tier.tagline}
        </p>
      )}
      
      {/* Philosophy line for Annual */}
      {tier.philosophy && (
        <p className="text-xs italic text-[var(--gold-dark)] mb-6 border-l-2 border-[var(--gold-primary)] pl-3 leading-relaxed">
          {tier.philosophy}
        </p>
      )}
      
      {/* Sections with Bullets */}
      {tier.sections && tier.sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className={`${sectionIndex > 0 ? 'pt-5 border-t border-[var(--border-light)]' : ''} ${isPremium ? 'mb-7' : 'mb-5'}`}>
          <p className={`text-sm font-semibold mb-3 ${isPremium ? 'text-[var(--gold-primary)]' : 'text-[var(--text-primary)]'}`}>
            {section.title}
          </p>
          <ul className="space-y-2.5">
            {section.bullets.map((bullet, bulletIndex) => (
              <li key={bulletIndex} className="flex items-start gap-3">
                <span className={`w-1 h-1 mt-2 rounded-full flex-shrink-0 ${isPremium ? 'bg-[var(--gold-primary)]' : 'bg-[var(--gold-dark)]'}`}></span>
                <span className={`text-xs leading-relaxed ${isPremium ? 'text-gray-300' : 'text-[var(--text-secondary)]'}`}>
                  {bullet}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      
      {/* Closing Line (for Tier 2) */}
      {tier.closingLine && (
        <div className="mb-5">
          <p className="text-xs italic text-[var(--text-secondary)] leading-relaxed">
            {tier.closingLine}
          </p>
        </div>
      )}
      
      {/* Note (for Tier 3) */}
      {tier.note && (
        <div className="mb-6 pt-5 border-t border-[var(--border-light)]">
          <p className="text-xs italic text-[var(--text-secondary)] leading-relaxed">
            {tier.note}
          </p>
        </div>
      )}
      
      {/* What this is NOT section */}
      {tier.notIncluded && tier.notIncluded.length > 0 && (
        <div className="mb-6 pt-5 border-t border-[var(--border-light)]">
          <p className={`text-sm font-semibold mb-3 ${isPremium ? 'text-[var(--gold-primary)]' : 'text-[var(--text-primary)]'}`}>
            What this is NOT
          </p>
          <ul className="space-y-2">
            {tier.notIncluded.map((item, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-red-500 flex-shrink-0">❌</span>
                <span className={`text-xs ${isPremium ? 'text-gray-300' : 'text-[var(--text-secondary)]'}`}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Limit Notice for Sovereign (removed 24 families mention) */}
      {tier.limitNotice && (
        <div className="mb-8 pt-6 border-t border-[var(--gold-primary)] opacity-70">
          <p className="text-xs italic text-[var(--gold-primary)] leading-relaxed">
            {tier.limitNotice}
          </p>
        </div>
      )}
      
      {/* CTA */}
      {isInternalLink ? (
        <Link
          to={tier.link}
          className={`w-full ${isPremium || isHighlighted || isBestValue ? 'btn-gold' : 'btn-primary'} justify-center`}
          onClick={() => trackInitiateCheckout(tier.name, tier.priceINR?.replace(/[^\d]/g, '') || 0, 'INR')}
        >
          {tier.cta}
          <ArrowRight size={16} />
        </Link>
      ) : (
        <a
          href={tier.link}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full ${isPremium || isHighlighted || isBestValue ? 'btn-gold' : 'btn-primary'} justify-center`}
          onClick={() => trackInitiateCheckout(tier.name, tier.priceINR?.replace(/[^\d]/g, '') || 0, 'INR')}
        >
          {tier.cta}
          <ArrowRight size={16} />
        </a>
      )}
      
      {/* CTA Note (subtle note below CTA) */}
      {tier.ctaNote && (
        <p className="text-xs text-[var(--text-light)] text-center mt-4 italic leading-relaxed px-2">
          {tier.ctaNote}
        </p>
      )}
      
      {/* Micro-filter under WhatsApp CTAs */}
      {showWhatsAppFilter && (
        <p className="text-xs text-[var(--text-light)] text-center mt-3">
          Serious families only • No quotes • No product selling
        </p>
      )}
    </div>
  );
};

const PathwaySection = () => {
  // Row 1: Tier 1, 2, 3, Annual (4 cards)
  // Row 2: Bundle, Sovereign (2 cards side-by-side)
  const row1Tiers = tierData.filter(t => ['tier1', 'tier2', 'tier3', 'priority'].includes(t.id));
  const row2Tiers = tierData.filter(t => ['bundle', 'sovereign'].includes(t.id));
  
  return (
    <section id="pathway" className="section-padding">
      <div className="container px-4 sm:px-10">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="heading-2 mb-6">
            The Pathway
          </h2>
          <p className="body-large text-[var(--text-secondary)] mb-4">
            From awareness to certainty. Choose how far you want to go.
          </p>
          <p className="body-regular text-[var(--text-light)]">
            Each tier is self-contained. No upsells. No dependencies.
          </p>
        </div>

        {/* Currency Toggle */}
        <CurrencyToggle />

        {/* Row 1: Core Pathway - Tier 1, 2, 3, Annual (4 cards side-by-side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {row1Tiers.map((tier) => (
            <TierCard key={tier.id} tier={tier} />
          ))}
        </div>

        {/* Row 2: Advanced Pathway - Bundle + Sovereign (2 cards side-by-side) */}
        {row2Tiers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {row2Tiers.map((tier) => (
              <TierCard key={tier.id} tier={tier} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PathwaySection;
